import { useState } from "react";
import { Button } from "../../components/ui/Button";
import { useToast } from "../../components/ui/Toast";
import { LabelWithHint } from "../../components/ui/Tooltip";
import type { TierFallback, TierWithGifts } from "../../lib/types";
import { ProductPicker } from "./ProductPicker";
import { useSetTierFallback } from "./useTiers";

const FALLBACK_HELP =
	"Handed over, free, in place of any gift in this tier that is out of stock along with its backups. Stock is checked live on the storefront. Like any gift, checkout makes it free in any cart that reaches this tier and contains it, so choose something you are happy to give away at that spend.";

const fallbackLabel = (fallback: TierFallback): string => {
	const title = fallback.productTitle ?? "Untitled product";
	if (fallback.selectVariant) return `${title} (shopper picks the variant)`;
	return fallback.variantTitle ? `${title} – ${fallback.variantTitle}` : title;
};

type TierFallbackFieldProps = {
	tier: TierWithGifts;
	campaignId: string;
};

export const TierFallbackField = ({ tier, campaignId }: TierFallbackFieldProps) => {
	const setFallback = useSetTierFallback(campaignId);
	const toast = useToast();
	const [picking, setPicking] = useState(false);
	const notify = { onError: (error: unknown) => toast.push((error as Error).message, "error") };

	return (
		<div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
			<div className="flex min-w-0 items-center gap-3">
				{tier.fallback?.thumbnailUrl ? (
					<img
						src={tier.fallback.thumbnailUrl}
						alt=""
						className="h-8 w-8 shrink-0 rounded-md border border-line object-cover"
					/>
				) : null}
				<span className="min-w-0">
					<LabelWithHint label="Fallback if out of stock">{FALLBACK_HELP}</LabelWithHint>
					<span className="block truncate text-xs text-ink-muted">
						{tier.fallback ? fallbackLabel(tier.fallback) : "None"}
					</span>
				</span>
			</div>
			<div className="flex shrink-0 items-center gap-2">
				<Button variant="outline" size="sm" onClick={() => setPicking(true)}>
					{tier.fallback ? "Change" : "Choose product"}
				</Button>
				{tier.fallback ? (
					<Button
						variant="ghost"
						size="sm"
						disabled={setFallback.isPending}
						onClick={() =>
							setFallback.mutate(
								{ tierId: tier.id, fallback: null },
								{ onSuccess: () => toast.push("Fallback removed"), ...notify },
							)
						}
					>
						Remove
					</Button>
				) : null}
			</div>
			<ProductPicker
				open={picking}
				onOpenChange={setPicking}
				title="Fallback if out of stock"
				variantHint="Pick the variant to hand over, or let the shopper choose. They get as many as the gift it stands in for."
				busy={setFallback.isPending}
				onPick={(picked) =>
					setFallback.mutate(
						{
							tierId: tier.id,
							fallback: {
								productId: picked.productId,
								variantId: picked.variantId,
								selectVariant: picked.selectVariant,
							},
						},
						{
							onSuccess: () => {
								toast.push("Fallback saved");
								setPicking(false);
							},
							...notify,
						},
					)
				}
			/>
		</div>
	);
};
